import React, { useCallback, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import RecipeFormFields from './RecipeFormFields';
import useRecipeEditors from '../../hooks/recipes/useRecipeEditors';
import useRecipeTagOptions from '../../hooks/recipes/useRecipeTagOptions';
import useRecipeFormFeedback from '../../hooks/forms/useRecipeFormFeedback';
import RecipeFormAlert from './RecipeFormAlert';
import {
  createRecipeWithOptionalImage,
  RecipeCreationWorkflowError,
} from '../../services/recipeCreationWorkflow';
import { Button, Card, CardTitle, FileInput, Form, FormField } from '../ui';

interface AddRecipeFormProps {
  onRecipeAdded: () => void;
}

const AddRecipeForm: React.FC<AddRecipeFormProps> = ({ onRecipeAdded }) => {
  const [title, setTitle] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { message, isError, resetFeedback, setFeedback } = useRecipeFormFeedback();

  const { author, token } = useAuth();
  const { ingredients, steps, ingredientsEditor, stepsEditor } = useRecipeEditors({
    initialIngredients: '',
    initialSteps: '',
  });
  const handleTagLoadError = useCallback((nextMessage: string) => {
    setFeedback(nextMessage, true);
  }, [setFeedback]);
  const { availableTags, setAvailableTags } = useRecipeTagOptions({
    onError: handleTagLoadError,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetFeedback();

    if (!author || !token) {
      setFeedback('Impossible de soumettre : auteur non authentifié.', true);
      return;
    }

    setSubmitting(true);

    try {
      await createRecipeWithOptionalImage({
        title,
        imageFile,
        ingredients,
        steps,
        authorId: author.id,
        selectedTagIds,
        token,
      });

      setFeedback('Recette ajoutée avec succès !');
      onRecipeAdded();
    } catch (error: unknown) {
      if (error instanceof RecipeCreationWorkflowError && error.step === 'upload') {
        console.error("Erreur lors de l'upload de l'image :", error);
        setFeedback(`Erreur lors de l'upload de l'image : ${error.message}`, true);
      } else {
        console.error("Erreur lors de l'ajout de la recette:", error);
        const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
        setFeedback(`Erreur lors de l'ajout : ${errorMessage}`, true);
      }
    } finally {
      setSubmitting(false);
    }
  };



  return (
    <Card shadow="sm" marginTop={4} padding={4}>
      <CardTitle as="h2" textAlign="center" marginBottom={4}>Ajouter une nouvelle recette</CardTitle>
      <Form onSubmit={handleSubmit}>
        <FormField label="Image (facultatif)" htmlFor="image">
          <FileInput id="image" accept="image/*" onChange={(e) => setImageFile(e.target.files ? e.target.files[0] : null)} />
        </FormField>
        <RecipeFormFields
          title={title}
          onTitleChange={setTitle}
          ingredientsEditor={ingredientsEditor}
          stepsEditor={stepsEditor}
          availableTags={availableTags}
          selectedTagIds={selectedTagIds}
          setAvailableTags={setAvailableTags}
          setSelectedTagIds={setSelectedTagIds}
          isAuthor={!!author}
          submitting={submitting}
          onMessage={setFeedback}
        />

        <Button type="submit" variant="primary" fullWidth marginBottom={5} disabled={submitting}>
          {submitting ? 'Envoi en cours...' : 'Ajouter la recette'}
        </Button>

        <RecipeFormAlert testId="form-message" message={message} isError={isError} />
      </Form>
    </Card>
  );
};

export default AddRecipeForm;
